import apiClient from '@disco/api/server'
import type { ClientLibraryId } from 'types/client-library'
import type {
  LicenseRequestSubmissionsPage,
  LicenseRequestSubmissionTrack,
  RequestFormVersion,
} from 'types/license-requests'
import type { GetState } from 'types/redux'

import { api } from './index'

export type {
  LicenseRequestSubmission,
  LicenseRequestSubmissionTrack,
} from 'types/license-requests'

export const PAGE_SIZE = 20

type QueryArg = {
  libraryId: ClientLibraryId
  ordering: string
  page: number
  fromDate?: string
  toDate?: string
}

export function getLicenseRequestSubmissionsExportUrl(args: {
  libraryId: ClientLibraryId
  ordering: string
  fromDate?: string
  toDate?: string
}): string {
  return apiClient.getLicenseRequestSubmissionsExportUrl(args)
}

export const licenseRequestsApi = api
  .enhanceEndpoints({
    addTagTypes: ['LicenseRequestSubmissions', 'RequestFormVersion', 'Track'],
  })
  .injectEndpoints({
    endpoints: build => ({
      getLicenseRequestSubmissions: build.query<
        LicenseRequestSubmissionsPage,
        QueryArg
      >({
        queryFn: async ({ libraryId, ordering, page, fromDate, toDate }) => {
          try {
            const data = await apiClient.getLicenseRequestSubmissions({
              libraryId,
              ordering,
              offset: (page - 1) * PAGE_SIZE,
              pageSize: PAGE_SIZE,
              fromDate,
              toDate,
            })
            return { data }
          } catch (error) {
            return { error: { status: 'FETCH_ERROR', error: String(error) } }
          }
        },
        providesTags: (result, _error, { libraryId }) => [
          { type: 'LicenseRequestSubmissions', id: libraryId },
          ...(result?.results ?? []).flatMap(submission =>
            submission.tracks.map(track => ({
              type: 'Track' as const,
              id: track.id,
            })),
          ),
        ],
      }),
      getRequestFormVersion: build.query<RequestFormVersion, number>({
        queryFn: async formVersionId => {
          try {
            const data = await apiClient.getRequestFormVersion(formVersionId)
            return { data }
          } catch (error) {
            return { error: { status: 'FETCH_ERROR', error: String(error) } }
          }
        },
        providesTags: (_result, _error, formVersionId) => [
          { type: 'RequestFormVersion', id: formVersionId },
        ],
      }),
    }),
  })

export const {
  useGetLicenseRequestSubmissionsQuery,
  useGetRequestFormVersionQuery,
} = licenseRequestsApi

/**
 * Patches every cached `getLicenseRequestSubmissions` page containing
 * `trackId` in place, wherever it appears (a track can be attached to
 * multiple submissions on the same page). Used to sync track metadata edits
 * made outside this API slice (the legacy track edit modal) back into the
 * submissions list without a full refetch.
 */
export function patchLicenseRequestSubmissionsTrack(
  dispatch: (...args: Array<any>) => any,
  getState: GetState,
  trackId: number,
  patch: (track: LicenseRequestSubmissionTrack) => void,
): void {
  const cachedPages = licenseRequestsApi.util
    .selectInvalidatedBy(getState(), [{ type: 'Track', id: trackId }])
    .filter(entry => entry.endpointName === 'getLicenseRequestSubmissions')

  cachedPages.forEach(({ originalArgs }) => {
    dispatch(
      licenseRequestsApi.util.updateQueryData(
        'getLicenseRequestSubmissions',
        originalArgs as QueryArg,
        draft => {
          draft.results.forEach(submission => {
            submission.tracks.forEach(track => {
              if (track.id === trackId) patch(track)
            })
          })
        },
      ),
    )
  })
}
